import { createStore, createLogger } from 'vuex'
import mergeWith from 'lodash/mergeWith'
import { vuexOidcCreateStoreModule } from 'vuex-oidc'

import { oidcSettings } from '@/config/oidc'
import logger from '@/plugins/logger'

// Persist a whitelist of app state to localStorage. Replaces the deprecated
// vuex-persistedstate, keeping its storage key ('vuex') and shape so state
// saved by earlier versions is still read. Auth tokens are intentionally not
// persisted here — vuex-oidc / oidc-client-ts manage them.
const PERSIST_KEY = 'vuex'
const PERSIST_PATHS = ['favoriteTrains', 'search', 'recentVisits']

// Long enough to cover the handful of trains someone actually follows, short
// enough that the strip stays one or two rows on a phone.
const MAX_RECENT_VISITS = 8

function persistedStatePlugin(store) {
  // Rehydrate persisted paths from a previous session, deep-merging over the
  // defaults but overwriting arrays wholesale (as vuex-persistedstate did).
  try {
    const saved = window.localStorage.getItem(PERSIST_KEY)
    if (saved) {
      const persisted = JSON.parse(saved)
      if (persisted && typeof persisted === 'object') {
        store.replaceState(
          mergeWith({}, store.state, persisted, (_, src) => Array.isArray(src) ? src : undefined)
        )
      }
    }
  } catch (e) {
    logger.warn(`Could not restore persisted state: ${e}`)
  }

  // Persist the whitelisted paths after every mutation.
  store.subscribe((_mutation, state) => {
    try {
      const snapshot = {}
      PERSIST_PATHS.forEach(path => { snapshot[path] = state[path] })
      window.localStorage.setItem(PERSIST_KEY, JSON.stringify(snapshot))
    } catch (e) {
      logger.warn(`Could not persist state: ${e}`)
    }
  })
}

function vuexPlugins() {
  if (import.meta.env.DEV) {
    return [persistedStatePlugin, createLogger({ logActions: true, logMutations: true, logger: console })]
  } else {
    return [persistedStatePlugin]
  }
}

export default createStore({
  actions: {
  },
  mutations: {
    toggleFavoriteTrain(state, payload) {
      logger.info(`toggleFavoriteTrain(${payload})`)

      var trainNumber = payload.train.toString()

      if (state.favoriteTrains.includes(trainNumber)) {
        state.favoriteTrains.splice(state.favoriteTrains.indexOf(trainNumber), 1)
      } else {
        state.favoriteTrains.push(trainNumber)
      }
    },

    recordVisit(state, payload) {
      var existing = state.recentVisits.findIndex(
        visit => visit.type === payload.type && visit.id === payload.id)

      // Carry a category learned on an earlier visit onto the new entry.
      // Without this, re-opening a train drops the label back to "Zug 70"
      // until its page has loaded and annotated it again.
      var category = existing !== -1 ? state.recentVisits[existing].category : undefined

      if (existing !== -1) {
        state.recentVisits.splice(existing, 1)
      }

      state.recentVisits.unshift({
        type: payload.type,
        id: payload.id,
        category: category,
        at: Date.now(),
      })
      state.recentVisits.splice(MAX_RECENT_VISITS)
    },

    // The router records a visit knowing only the route parameter; the train's
    // category is known only once its page has loaded. Attach it in place —
    // deliberately without reordering or restamping `at`, so merely finishing a
    // load cannot rewrite the history's order.
    annotateVisit(state, payload) {
      var entry = state.recentVisits.find(
        visit => visit.type === payload.type && visit.id === payload.id)

      if (entry) {
        entry.category = payload.category
      }
    },

    clearRecentVisits(state) {
      state.recentVisits = []
    },

    setAdmin(state, admin) {
      state.admin = admin
    },

    setAnonymousHistoryDays(state, days) {
      state.anonymousHistoryDays = days
    },

    updateSearchRange(state, range) {
      state.search.range = range
    },

    updateSearchStations(state, { from, to }) {
      state.search.from = from
      state.search.to = to
    },

    updateConnectionDays(state, days) {
      state.search.connectionDays = days
    },

    setNotificationSubscriptions(state, subscriptions) {
      state.notifications.subscriptions = subscriptions
    },

    upsertNotificationSubscription(state, subscription) {
      var list = state.notifications.subscriptions.filter(existing => existing.tripId !== subscription.tripId)
      list.push(subscription)
      state.notifications.subscriptions = list
    },

    removeNotificationSubscription(state, tripId) {
      state.notifications.subscriptions = state.notifications.subscriptions.filter(existing => existing.tripId !== tripId)
    },
  },
  modules: {
    // Every route is browsable without signing in. vuex-oidc still restores a stored session on
    // each navigation (oidcCheckAccess dispatches oidcWasAuthenticated for a valid user), it just
    // never redirects to the identity provider on its own — App.vue offers that as an explicit
    // action. Using the predicate rather than meta.isPublic per route matters: App.vue's nav
    // drawer lists routes *because* they have `meta`, so blanket-adding meta would leak internal
    // routes into it.
    oidcStore: vuexOidcCreateStoreModule(oidcSettings, {
      namespaced: true,
      isPublicRoute: () => true,
    }),
  },
  plugins: vuexPlugins(),
  state: {
    favoriteTrains: [],

    // Train and vehicle pages this browser has opened, most recent first. Fed
    // by the router, so every route in counts — search, favourites, a link in a
    // trip row, or a pasted URL.
    recentVisits: [],

    admin: false,

    // How many days of history this caller may read, from GET /api/me. null = unrestricted
    // (signed in, or a deployment with no cap). Server state — deliberately not in
    // PERSIST_PATHS, so it is never served stale from localStorage.
    anonymousHistoryDays: null,

    // This device's push subscriptions, as GET from the backend. Server state like
    // anonymousHistoryDays — deliberately not in PERSIST_PATHS; it is re-read on start.
    notifications: {
      subscriptions: [],
    },

    search: {
      range: 7,

      // Last searched station pair as {evaId, name}, so the connection page comes
      // back with it filled in *and* readable — an eva id alone would show as
      // "8000105". Rides the existing `search` persistence; older localStorage
      // simply has no such keys (or, from one intermediate version, a bare eva id
      // string) and Connections.vue normalises both on read.
      from: null,
      to: null,

      // The connection page's own rating window, in days. Deliberately separate from
      // `range`: that one drives the punctuality pages' history depth, and coupling
      // the two made this page inherit a 7-day default the backend's minimum sample
      // size mostly cannot rate. 30 is also the backend's own default.
      connectionDays: 30,
    },
  },
})
